/**
 * Phase 1.5 Milestone D3 — typed adapter for import-duplicate payloads.
 *
 * Backend contract (see backend/app/services/import_csv.py validate_rows,
 * `import_duplicates` insert block):
 *
 *   {
 *     id, tenant_id, job_id, row_id,
 *     match_rule: string,
 *     match_strength: "high" | "medium" | "low",
 *     match_score: number,
 *     existing_contact_id: string,
 *     existing_summary: { first_name, last_name, email(masked), phone(masked), lifecycle_status },
 *     source_summary:   { first_name, last_name, email(masked), phone(masked), lifecycle_status },
 *     conflicting_fields: string[],
 *     non_conflicting_fields: string[],
 *     suggested_resolutions: string[],
 *     validation_status: string,
 *     version: number,
 *   }
 *
 * The email/phone in `existing_summary` are already masked server-side
 * (`_mask_email`, `_mask_phone`) so no raw PII round-trips. The frontend
 * MUST NOT try to fetch a full Contact by id to enrich this view — the
 * backend intentionally exposes only these safe summary fields for the
 * "Compare Incoming Data" surface.
 */

import { isKnownResolution, Resolution } from "./rowContract";

export type MatchStrength = "high" | "medium" | "low";
const KNOWN_STRENGTHS: MatchStrength[] = ["high", "medium", "low"];

export const COMPARE_FIELDS = [
  "first_name",
  "last_name",
  "email",
  "phone",
  "lifecycle_status",
] as const;

export type CompareField = (typeof COMPARE_FIELDS)[number];

export const COMPARE_FIELD_LABELS: Record<CompareField, string> = {
  first_name: "First Name",
  last_name: "Last Name",
  email: "Email",
  phone: "Phone",
  lifecycle_status: "Lifecycle",
};

export type CandidateSummary = Partial<Record<CompareField, string | null>>;

export type NormalizedDuplicate = {
  id: string;
  rowId: string;
  existingContactId: string;
  matchRule: string;
  matchStrength: MatchStrength;
  matchScore: number;
  existingSummary: CandidateSummary;
  sourceSummary: CandidateSummary;
  conflictingFields: CompareField[];
  nonConflictingFields: CompareField[];
  suggestedResolutions: Resolution[];
  version: number;
};

function pickSummary(raw: unknown): CandidateSummary {
  if (!raw || typeof raw !== "object") return {};
  const src = raw as Record<string, unknown>;
  const out: CandidateSummary = {};
  for (const f of COMPARE_FIELDS) {
    if (f in src) {
      const v = src[f];
      out[f] = typeof v === "string" ? v : v == null ? null : String(v);
    }
  }
  return out;
}

function pickCompareFieldList(raw: unknown): CompareField[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((v): v is CompareField =>
    typeof v === "string" && (COMPARE_FIELDS as readonly string[]).includes(v),
  );
}

export function normalizeDuplicate(raw: unknown): NormalizedDuplicate | null {
  if (!raw || typeof raw !== "object") return null;
  const src = raw as Record<string, unknown>;
  const id = typeof src.id === "string" ? src.id : null;
  const rowId = typeof src.row_id === "string" ? src.row_id : null;
  const existingId =
    typeof src.existing_contact_id === "string" ? src.existing_contact_id : null;
  if (!id || !rowId || !existingId) return null;

  const strengthRaw = typeof src.match_strength === "string" ? src.match_strength : "";
  const matchStrength = KNOWN_STRENGTHS.includes(strengthRaw as MatchStrength)
    ? (strengthRaw as MatchStrength)
    : "low";

  const suggestedResolutions = Array.isArray(src.suggested_resolutions)
    ? (src.suggested_resolutions.filter(isKnownResolution) as Resolution[])
    : [];

  return {
    id,
    rowId,
    existingContactId: existingId,
    matchRule: typeof src.match_rule === "string" ? src.match_rule : "",
    matchStrength,
    matchScore:
      typeof src.match_score === "number" && Number.isFinite(src.match_score)
        ? src.match_score
        : 0,
    existingSummary: pickSummary(src.existing_summary),
    sourceSummary: pickSummary(src.source_summary),
    conflictingFields: pickCompareFieldList(src.conflicting_fields),
    nonConflictingFields: pickCompareFieldList(src.non_conflicting_fields),
    suggestedResolutions,
    version:
      typeof src.version === "number" && Number.isFinite(src.version)
        ? src.version
        : 0,
  };
}

export function normalizeDuplicates(raw: unknown): NormalizedDuplicate[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((d) => normalizeDuplicate(d))
    .filter((d): d is NormalizedDuplicate => !!d);
}

/**
 * Group duplicate candidates by their originating row_id — the review UI
 * fetches one full list per job and needs O(1) lookup by row.
 */
export function groupDuplicatesByRow(
  duplicates: NormalizedDuplicate[],
): Record<string, NormalizedDuplicate[]> {
  const out: Record<string, NormalizedDuplicate[]> = {};
  for (const d of duplicates) {
    (out[d.rowId] ||= []).push(d);
  }
  return out;
}

/** Union of all `suggested_resolutions` across candidates for one row. */
export function suggestedForRow(
  duplicates: NormalizedDuplicate[] | undefined,
): Resolution[] {
  if (!duplicates || duplicates.length === 0) return [];
  const set = new Set<Resolution>();
  for (const d of duplicates) for (const r of d.suggestedResolutions) set.add(r);
  return [...set];
}
